import { Ref, computed, ref } from "vue";

import { useForm } from '@/composable/form/form';
import { DialogService } from "@/service/DialogService";
import dayjs from "dayjs";
import OptionConst from "@/const/Option";
import { Furikyuu, Jikangai, Kekkin, Kibiki, KoukyuuData, KyuukeiJikangai, Nittyoku, Yuukyuu } from "@/types/Shinsei";
import { SoumuSyuuseiReq, SyuuseiDataDetail } from "@/types/SoumuSyuusei";
import { DakokuService } from "@/service/DakokuService";
import { AxiosError } from "axios";
import { FormUtil } from "@/utils/FormUtil";
import { KoyouKeiyakuTimeAndHayabanOsobanOption } from "@/types/KoyouKeiyakuTime";

export function useDakokuSoumuSyuusei() {
  const { removeAllError } = useForm()
  const loading = ref(0)
  const isError = ref(false)

  const update = async (form: Ref<SoumuSyuuseiReq>, isDelete: boolean = false) => {
    let dialogResult = false
    if (!isDelete) {
      const confRes = await DialogService.question({
        html: '<p><span class="bold">入力内容を保存しますか？</span><br><br>※以前の打刻とそれに紐づく申請も削除されます<br><br><span class="bold">【注意】 この操作は元に戻せません</span></p>',
        showCancelButton: true,
        cancelButtonText: 'いいえ',
        confirmButtonText: '保存する',
        allowOutsideClick: false,
      })
      dialogResult = confRes.isConfirmed
    } else {
      const confRes = await DialogService.question({
        html: '<p><span class="bold">打刻取消を実行しますか？</span><br><br>※打刻とそれに紐づく申請も削除されます<br>※入力中の内容は破棄されます<br><br><span class="bold">【注意】 この操作は元に戻せません</span></p>',
        showCancelButton: true,
        cancelButtonText: 'いいえ',
        confirmButtonText: '取消を実行する',
        allowOutsideClick: false,
      })
      dialogResult = confRes.isConfirmed
    }

    if (!dialogResult) {
      return false
    }

    loading.value++

    removeAllError(form.value.syuuseiData)
    removeAllError(form.value.shinsei)
    const shinseiKeys = Object.keys(form.value.shinsei) as (keyof typeof form.value.shinsei)[]
    shinseiKeys.forEach(function (k, i) {
      if (typeof form.value.shinsei[k] === 'object') {
        removeAllError(form.value.shinsei[k])
      }
    })

    // isDeleteの内容で打刻取消の申請とするか否かを決定する
    form.value.syuuseiData.is_torikeshi = isDelete

    let result = false
    const srv = new DakokuService();
    try {
      const res = await srv.updateSoumuSyuusei(form.value)

      result = true
    } catch (e) {
      const err = e as AxiosError<any>;
      FormUtil.setLaravelError(form.value, err);
    } finally {
      loading.value--
    }
    return result
  }

  const yuukyuuTime = (type: number | null, time: string | null, yuukyuu: Ref<Yuukyuu>) => {
    let resTime
    if(!type || !time){
      resTime = '0.00'
    }

    const nowYMD = dayjs().format('YYYY/MM/DD')
    const yuukyuuTime = dayjs(nowYMD + ' ' + time)
    resTime = type == OptionConst.SHINSEI_DIV.FULL_DAY ? yuukyuuTime : yuukyuuTime.subtract(4, 'h')
    yuukyuu.value.yuukyuu_times = resTime.format('hh:mm')
    return (resTime.hour() + (Number(resTime.minute()) / 60)).toFixed(2)
  }

  const isLoading = computed(() => {
      return !(loading.value === 0 || isError.value);
  })

  const setYuukyuu = (date: string) => {
    return <Yuukyuu> {
      start_date: date,
      time: null,
      yuukyuu_tani_type: null,
      yuukyuu_times: null,
    }
  }

  const setJikangai = (date: string, koyouTime: KoyouKeiyakuTimeAndHayabanOsobanOption | null) => {
    let startTime = null
    if(koyouTime){
      const endTimeStartPos = koyouTime.label.indexOf("～") + 1
      const endTimeEndPos = koyouTime.label.indexOf("（")
      startTime = koyouTime.label.substring(endTimeStartPos, endTimeEndPos)
    }
    return <Jikangai> {
      jikangai_date: date,
      start_date: date,
      end_date: date,
      end_datetime: null,
      end_time: null,
      gyoumu_notes: null,
      iin_close_time: null,
      start_datetime: null,
      start_time: startTime
    }
  }

  const setFurikyuuFuride = (date: string, isFuride: boolean, syuuseiData: SyuuseiDataDetail) => {
    let res = <Furikyuu> {
      end_time: null,
      furikyuu1_date: null,
      furikyuu2_date: null,
      furikyuutani1_type: null,
      furikyuutani2_type: null,
      kyuukei_time: null,
      start_time: null,
      syukkin_date: null,
      syukkin_furidetani_type: null,
    }

    if(isFuride){
      res.syukkin_furidetani_type = OptionConst.SHINSEI_DIV.FULL_DAY
      res.syukkin_date = date
      res.start_time = syuuseiData.syukkin_time
      res.end_time = syuuseiData.taikin_time
      res.kyuukei_time = syuuseiData.kyuukei_time
    }else{
      res.furikyuu1_date = date
      res.furikyuutani1_type = OptionConst.SHINSEI_DIV.FULL_DAY
    }

    return res
  }

  const setNittyoku = (date: string) => {
    return <Nittyoku>{
      shinsei_date: date,
    }
  }

  const setKekkin = (date: string) => {
    return <Kekkin>{
      start_date: date,
      end_date: date,
      kekkin_days: 1,
    }
  }

  const setKibiki = (date: string) => {
    return <Kibiki>{
      kibiki_start_date: date,
      kibiki_tani_type: OptionConst.SHINSEI_DIV.FULL_DAY,
      kibiki_times: null,
      kojin_name: null,
      kojin_kana: null,
      tsuzukigara: null,
      sikyo_date: null,
      tsuya_date: null,
      kokubetsushiki_date: null,
      kaijou_name: null,
      kaijou_address: null,
      kaijou_tel: null,
    }
  }

  const setKyuukeiJikangai = (date: string) => {
    return <KyuukeiJikangai>{
      kyuukei_jikangai_date: date,
      kyuukei_jikangai_times: null,
      gyoumu_notes: null,
    }
  }

  const setKoukyuu = (date: string, koyouKeiyakuId: number, syuugyouBasyoId: number) => {
    return <KoukyuuData>{
      koukyuu_date: date,
      koukyuu_tani_type: OptionConst.SHINSEI_DIV.FULL_DAY,
      koyou_keiyaku_id: koyouKeiyakuId,
      mst_syuugyou_basyo_id: syuugyouBasyoId
    }
  }


  // 下記コメントにあるとおり仕様変更によりALLのみとなるため廃止
  // MEDICA_AS-1104 タイムシート 総務修正ダイアログ 有休の時間計算が誤っている
  //https://imp-hd.backlog.com/view/MEDICA_AS-1104#comment-325638704
  //
  // const getKinmuTypeForYuukyuu = (sashihikiTime: string | null, koyouType: number | null, wotkTimeCount: number) => {
  //   const sashihikiTimes = DateUtil.timeToDate(sashihikiTime ?? '')
  //   if ((koyouType != OptionConst.KOYOU_KEITAI_TYPE.HAKENSYAIN_PART && koyouType != OptionConst.KOYOU_KEITAI_TYPE.KEIYAKUSYAIN_PART)
  //     || sashihikiTimes >= DateUtil.timeToDate('7:00:00')) {
  //     // 有給差し引き時間 ≧ 7時間
  //     return OptionConst.YUUKYUU_TYPE.ALL
  //   }
  //   if (wotkTimeCount == 1) {
  //     // 雇用契約就業時間が１レコードのみ
  //     return OptionConst.YUUKYUU_TYPE.NO_HARF
  //   }
  //   // 曜日によって勤務時間が変わる
  //   return OptionConst.YUUKYUU_TYPE.FIX_TIME
  // }

  return {
    isLoading,
    isError,
    update,
    yuukyuuTime,
    setYuukyuu,
    setJikangai,
    // getKinmuTypeForYuukyuu,
    removeAllError,
    setFurikyuuFuride,
    setNittyoku,
    setKibiki,
    setKekkin,
    setKyuukeiJikangai,
    setKoukyuu
  }
}
